import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2, Archive, Check, X, AlertTriangle, CreditCard, ArrowLeft } from 'lucide-react';
import api from '../components/api';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';

const ModalitePaiementPage = () => {
  const navigate = useNavigate();
  // États pour les onglets
  const [activeTab, setActiveTab] = useState('modalites');
  
  // États pour les modalités de paiement
  const [modalites, setModalites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedModalite, setSelectedModalite] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
    modalite: null
  });
  const [formData, setFormData] = useState({
    nombre_tranches: '',
    tranches: []
  });
  const [annees, setAnnees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // États pour les modes de paiement
  const [modesPaiement, setModesPaiement] = useState([]);
  const [showModePaiementModal, setShowModePaiementModal] = useState(false);
  const [selectedModePaiement, setSelectedModePaiement] = useState(null);
  const [modePaiementForm, setModePaiementForm] = useState({
    libelle: ''
  });

  useEffect(() => {
    if (activeTab === 'modalites') {
      fetchModalites();
      fetchAnnees();
    } else {
      fetchModesPaiement();
    }
  }, [activeTab]);

  const fetchModalites = async () => {
    try {
      const response = await api.get('/niveau/liste-modalites/');
      setModalites(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des modalités:', error);
      toast.error('Erreur lors de la récupération des modalités');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnees = async () => {
    try {
      const response = await api.get('/etablissement/annees-academiques/');
      setAnnees(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des années:', error);
      toast.error('Erreur lors de la récupération des années académiques');
    }
  };

  const fetchModesPaiement = async () => {
    try {
      setLoading(true);
      const response = await api.get('/inscription/liste-modes-paiement/');
      setModesPaiement(response.data.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des modes de paiement:', error);
      toast.error('Erreur lors de la récupération des modes de paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const selectedAnneeId = formData.tranches[0]?.annee;
      const dataToSend = {
        nombre_tranches: parseInt(formData.nombre_tranches),
        annee: parseInt(selectedAnneeId),
        tranches: formData.tranches.map((tranche) => ({
          libelle: tranche.libelle,
          pourcentage: parseFloat(tranche.pourcentage),
          date: tranche.date
        }))
      };

      if (selectedModalite) {
        // Mise à jour groupée des modalités
        await api.put(`/niveau/liste-modalites/${selectedAnneeId}/`, dataToSend);
        toast.success('Modalités mises à jour avec succès');
      } else {
        // Création de nouvelles modalités
        await api.post('/niveau/liste-modalites/', dataToSend);
        toast.success('Modalités créées avec succès');
      }
      setShowModal(false);
      fetchModalites();
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if (error.response?.data) {
        const errors = error.response.data;
        if (typeof errors === 'object') {
          Object.entries(errors).forEach(([field, message]) => {
            toast.error(`${field}: ${Array.isArray(message) ? message.join(', ') : message}`);
          });
        } else {
          toast.error(errors.detail || 'Erreur lors de la sauvegarde des modalités');
        }
      }
    }
  };

  const openConfirmDialog = (title, message, action, modalite) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      action,
      modalite
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      action: null,
      modalite: null
    });
  };

  const handleDelete = async (modalite) => {
    try {
      await api.delete(`/niveau/liste-modalites/${modalite.annee.id}/`);
      toast.success('Modalités supprimées avec succès');
      fetchModalites();
      closeConfirmDialog();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression des modalités');
    }
  };

  const handleEdit = (modalite) => {
    // Récupérer toutes les modalités pour cette année académique
    const modalitesAnnee = modalites.filter(m => m.annee.id === modalite.annee.id);
    
    setSelectedModalite(modalitesAnnee);
    setFormData({
      nombre_tranches: modalitesAnnee.length,
      tranches: modalitesAnnee.map(m => ({
        libelle: m.libelle,
        pourcentage: m.pourcentage,
        date: m.date ? new Date(m.date).toISOString().split('T')[0] : '',
        annee: modalite.annee.id
      }))
    });
    setShowModal(true);
  };

  const handleNombreTrancheChange = (e) => {
    const nombre = parseInt(e.target.value) || 0;
    if (nombre >= 0) {
      // Créer ou ajuster le tableau des tranches
      const newTranches = [...Array(nombre)].map((_, index) => ({
        libelle: formData.tranches[index]?.libelle || `Tranche ${index + 1}`,
        pourcentage: formData.tranches[index]?.pourcentage || '',
        date: formData.tranches[index]?.date || '',
        annee: formData.tranches[index]?.annee || ''
      }));

      setFormData(prev => ({
        ...prev,
        nombre_tranches: nombre,
        tranches: newTranches
      }));
    }
  };

  const handleTrancheChange = (index, field, value) => {
    const newTranches = [...formData.tranches];
    newTranches[index] = {
      ...newTranches[index],
      [field]: value
    };

    // Vérification du total des pourcentages lors de la modification
    if (field === 'pourcentage') {
      const total = newTranches.reduce((acc, curr) => acc + (parseFloat(curr.pourcentage) || 0), 0);
      if (total > 100) {
        toast.error('Le total des pourcentages ne peut pas dépasser 100%');
        // Réinitialiser la valeur si le total dépasse 100%
        newTranches[index].pourcentage = '';
      }
    }

    setFormData(prev => ({
      ...prev,
      tranches: newTranches
    }));
  };

  const handleAnneeChange = (value) => {
    const newTranches = formData.tranches.map(t => ({
      ...t,
      annee: value
    }));
    setFormData(prev => ({
      ...prev,
      tranches: newTranches
    }));
  };

  const validateForm = () => {
    if (!formData.nombre_tranches || formData.nombre_tranches <= 0) {
      toast.error('Le nombre de tranches doit être supérieur à 0');
      return false;
    }

    const selectedAnneeId = formData.tranches[0]?.annee;
    if (!selectedAnneeId) {
      toast.error('Veuillez sélectionner une année académique');
      return false;
    }

    // Vérifier que toutes les tranches ont des valeurs valides
    for (let i = 0; i < formData.tranches.length; i++) {
      const tranche = formData.tranches[i];
      if (!tranche.libelle) {
        toast.error(`Le libellé de la tranche ${i + 1} est requis`);
        return false;
      }
      if (!tranche.pourcentage || isNaN(tranche.pourcentage) || tranche.pourcentage <= 0) {
        toast.error(`Le pourcentage de la tranche ${i + 1} doit être un nombre positif`);
        return false;
      }
      if (!tranche.date) {
        toast.error(`La date limite de la tranche ${i + 1} est requise`);
        return false;
      }
    }

    // Vérifier que la somme des pourcentages est exactement égale à 100
    const somme = formData.tranches.reduce((acc, curr) => acc + (parseFloat(curr.pourcentage) || 0), 0);
    if (somme !== 100) {
      toast.error(`La somme des pourcentages doit être exactement égale à 100% (actuellement: ${somme}%)`);
      return false;
    }

    return true;
  };

  const resetForm = () => {
    setSelectedModalite(null);
    setFormData({
      nombre_tranches: '',
      tranches: []
    });
  };

  const filteredModalites = modalites.filter(modalite =>
    modalite.libelle.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedModalites = filteredModalites.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredModalites.length / itemsPerPage);

  const handleModePaiementSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedModePaiement) {
        await api.put(`/inscription/liste-modes-paiement/${selectedModePaiement.id}/`, modePaiementForm);
        toast.success('Mode de paiement modifié avec succès');
      } else {
        await api.post('/inscription/liste-modes-paiement/', modePaiementForm);
        toast.success('Mode de paiement créé avec succès');
      }
      setShowModePaiementModal(false);
      fetchModesPaiement();
      resetModePaiementForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde du mode de paiement');
    }
  };

  const handleDeleteModePaiement = async (mode) => {
    try {
      await api.delete(`/inscription/liste-modes-paiement/${mode.id}/`);
      toast.success('Mode de paiement supprimé avec succès');
      fetchModesPaiement();
      closeConfirmDialog();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression du mode de paiement');
    }
  };

  const resetModePaiementForm = () => {
    setSelectedModePaiement(null);
    setModePaiementForm({ libelle: '' });
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <CreditCard className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion des Paiements</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les modalités et modes de paiement
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              <div className="max-w-7xl mx-auto">
                {/* Onglets */}
                <div className="mb-6">
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                      <button
                        onClick={() => setActiveTab('modalites')}
                        className={`${
                          activeTab === 'modalites'
                            ? 'border-[#202654] text-[#202654]'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                      >
                        Modalités de Paiement
                      </button>
                      <button
                        onClick={() => setActiveTab('modes')}
                        className={`${
                          activeTab === 'modes'
                            ? 'border-[#202654] text-[#202654]'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                      >
                        Modes de Paiement
                      </button>
                    </nav>
                  </div>
                </div>

                {/* Contenu des onglets */}
                {activeTab === 'modalites' ? (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <div className="w-64">
                        <input
                          type="text"
                          placeholder="Rechercher une modalité..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
                        />
                      </div>
                      <button
                        onClick={() => {
                          resetForm();
                          setShowModal(true);
                        }}
                        className="inline-flex items-center bg-[#202654] text-white px-4 py-2 rounded-lg shadow-md hover:bg-[#2d347d] transition-all duration-200"
                      >
                        <Plus className="h-5 w-5 mr-2" />
                        Nouvelle Modalité
                      </button>
                    </div>

                    {loading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : paginatedModalites.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500 mb-4">Aucune modalité de paiement trouvée</p>
                        <button
                          onClick={() => {
                            resetForm();
                            setShowModal(true);
                          }}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                        >
                          Ajouter une modalité
                        </button>
                      </div>
                    ) : (
                      <div className="bg-white rounded-lg shadow overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Libellé
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Pourcentage
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date limite
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedModalites.map((modalite) => (
                              <tr key={modalite.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {modalite.libelle}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {modalite.pourcentage}%
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(modalite.date).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => handleEdit(modalite)}
                                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                                  >
                                    <Pencil className="h-5 w-5" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(modalite)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <Trash2 className="h-5 w-5" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {totalPages > 1 && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between bg-white px-4 py-3 sm:px-6 rounded-lg shadow">
                          <div className="flex flex-1 justify-between sm:hidden">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Précédent
                            </button>
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Suivant
                            </button>
                          </div>
                          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm text-gray-700">
                                Affichage de{' '}
                                <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span>
                                {' '}à{' '}
                                <span className="font-medium">
                                  {Math.min(currentPage * itemsPerPage, filteredModalites.length)}
                                </span>
                                {' '}sur{' '}
                                <span className="font-medium">{filteredModalites.length}</span>
                                {' '}résultats
                              </p>
                            </div>
                            <div>
                              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                <button
                                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                  disabled={currentPage === 1}
                                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <span className="sr-only">Précédent</span>
                                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                {[...Array(totalPages)].map((_, index) => (
                                  <button
                                    key={index + 1}
                                    onClick={() => setCurrentPage(index + 1)}
                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                      currentPage === index + 1
                                        ? 'z-10 bg-[#202654] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#202654]'
                                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                    }`}
                                  >
                                    {index + 1}
                                  </button>
                                ))}
                                <button
                                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                  disabled={currentPage === totalPages}
                                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <span className="sr-only">Suivant</span>
                                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </nav>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <div className="w-64">
                        <input
                          type="text"
                          placeholder="Rechercher un mode de paiement..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
                        />
                      </div>
                      <button
                        onClick={() => {
                          resetModePaiementForm();
                          setShowModePaiementModal(true);
                        }}
                        className="inline-flex items-center bg-[#202654] text-white px-4 py-2 rounded-lg shadow-md hover:bg-[#2d347d] transition-all duration-200"
                      >
                        <Plus className="h-5 w-5 mr-2" />
                        Nouveau Mode de Paiement
                      </button>
                    </div>

                    {loading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : modesPaiement.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500 mb-4">Aucun mode de paiement trouvé</p>
                        <button
                          onClick={() => {
                            resetModePaiementForm();
                            setShowModePaiementModal(true);
                          }}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                        >
                          Ajouter un mode de paiement
                        </button>
                      </div>
                    ) : (
                      <div className="bg-white rounded-lg shadow overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Libellé
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date d'ajout
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {modesPaiement
                              .filter(mode => mode.libelle.toLowerCase().includes(searchTerm.toLowerCase()))
                              .map((mode) => (
                                <tr key={mode.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{mode.libelle}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                      {new Date(mode.date_ajout).toLocaleDateString()}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                      onClick={() => {
                                        setSelectedModePaiement(mode);
                                        setModePaiementForm({ libelle: mode.libelle });
                                        setShowModePaiementModal(true);
                                      }}
                                      className="text-blue-600 hover:text-blue-900 mr-3 transition-colors"
                                    >
                                      <Pencil className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() => openConfirmDialog(
                                        'Supprimer le mode de paiement',
                                        `Êtes-vous sûr de vouloir supprimer le mode de paiement "${mode.libelle}" ? Cette action est irréversible.`,
                                        handleDeleteModePaiement,
                                        mode
                                      )}
                                      className="text-red-600 hover:text-red-900 transition-colors"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="relative bg-white rounded-xl w-full max-w-4xl mx-auto shadow-xl">
              <div className="px-6 py-4 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">
                    {selectedModalite ? 'Modifier les modalités' : 'Nouvelles modalités'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-4">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre de tranches
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.nombre_tranches}
                      onChange={handleNombreTrancheChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202654]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Année académique
                    </label>
                    <select
                      value={formData.tranches[0]?.annee || ''}
                      onChange={(e) => handleAnneeChange(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202654]"
                      required
                    >
                      <option value="">Sélectionner une année</option>
                      {annees.map((annee) => (
                        <option key={annee.id} value={annee.id}>
                          {annee.libelle}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {formData.tranches.length > 0 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {formData.tranches.map((tranche, index) => (
                        <div key={index} className="border p-4 rounded-lg">
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Libellé tranche {index + 1}
                            </label>
                            <input
                              type="text"
                              value={tranche.libelle}
                              onChange={(e) => handleTrancheChange(index, 'libelle', e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Pourcentage (%)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={tranche.pourcentage}
                              onChange={(e) => handleTrancheChange(index, 'pourcentage', e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Date limite
                            </label>
                            <input
                              type="date"
                              value={tranche.date}
                              onChange={(e) => handleTrancheChange(index, 'date', e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className={`text-sm ${
                        formData.tranches.reduce((acc, curr) => acc + (parseFloat(curr.pourcentage) || 0), 0) === 100 
                          ? 'text-green-800' 
                          : 'text-[#202654]'
                      }`}>
                        Total: {formData.tranches.reduce((acc, curr) => acc + (parseFloat(curr.pourcentage) || 0), 0)}%
                        {formData.tranches.reduce((acc, curr) => acc + (parseFloat(curr.pourcentage) || 0), 0) !== 100 && (
                          <span className="text-red-600 ml-2">
                            (Doit être exactement égal à 100%)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#2d347d] transition-colors flex items-center"
                  >
                    {loading && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {selectedModalite ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showModePaiementModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="relative bg-white rounded-xl w-full max-w-md mx-auto shadow-xl">
              <div className="px-6 py-4 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">
                    {selectedModePaiement ? 'Modifier le mode de paiement' : 'Nouveau mode de paiement'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowModePaiementModal(false);
                      resetModePaiementForm();
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleModePaiementSubmit} className="px-6 py-4">
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Libellé
                  </label>
                  <input
                    type="text"
                    value={modePaiementForm.libelle}
                    onChange={(e) => setModePaiementForm({ libelle: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202654]"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModePaiementModal(false);
                      resetModePaiementForm();
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#2d347d] transition-colors"
                  >
                    {selectedModePaiement ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {confirmDialog.open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-orange-500 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-800">{confirmDialog.title}</h2>
                </div>
                <p className="text-gray-600 mb-6">{confirmDialog.message}</p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={closeConfirmDialog}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => confirmDialog.action(confirmDialog.modalite)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ModalitePaiementPage;
